import {
  QuestionOption,
  QuestionOptionType,
} from '@doltech/ui/lib/figma/CircleText/QuestionOption';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { OptionsListDrawer } from '@doltech/ui/lib/figma/Mobile/OptionsList/OptionsListDrawer';
import { MobileSpot } from '@doltech/ui/lib/figma/QuestionWithSpots/DragIn';
import { Spot } from '@doltech/ui/lib/figma/QuestionWithSpots/types';
import { useCycle } from '@doltech/ui/lib/hooks/useCycle';
import { numberToABC } from '@doltech/utils/lib/text';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { DoTestDragInInstance } from '../../../model/Question/do-test-drag-in.store';

interface DragInProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestDragInInstance;
}

export const QuestionDragInMobile = observer((props: DragInProps) => {
  const { data } = props;
  const { no, questions, answer, image, options, userAnswers } = data;
  const [scaledImageWidth, setScaledImageWidth] = React.useState(undefined);
  const [scaledImageHeight, setScaledImageHeight] = React.useState(undefined);
  const [isOpen, toggleOpen] = useCycle(false, true);
  const [id, setId] = React.useState(undefined);
  const imgElement = React.useRef(null);

  const answerQuestion = (item) => {
    toggleOpen();
    answer(id, item.key);
  };

  const onItemClick = (item) => {
    setId(item);
    toggleOpen();
  };

  const onClose = () => {
    toggleOpen();
  };

  const onRemoveAnswer = (e, idx) => {
    e.stopPropagation();
    answer(idx, null);
  };

  return (
    <>
      <ContentContainer className="image-box">
        <ImageWithSpotsContainer>
          <img
            loading="lazy"
            width={image.width}
            height={image.height}
            src={image.url}
            ref={imgElement}
            onLoad={() => {
              setScaledImageWidth(imgElement.current.clientWidth);
              setScaledImageHeight(imgElement.current.clientHeight);
            }}
            alt="dragin-spot"
          />
          {questions.map((question: Spot, idx: number) => {
            return (
              <MobileSpot
                question={{
                  x: scaleDraggingPoint(image.width, scaledImageWidth, question.x),
                  y: scaleDraggingPoint(image.height, scaledImageHeight, question.y),
                }}
                idx={idx}
                onRemove={() => {}}
                key={question.id}
                no={no}
                image={image}
                answered={userAnswers}
              />
            );
          })}
        </ImageWithSpotsContainer>
      </ContentContainer>
      <ContentContainer className="options">
        {questions.map((option: any, idx) => {
          const isAnswered = typeof userAnswers[idx] === 'number';
          if (isAnswered) {
            return (
              <DragOutMobileContainer onClick={() => onItemClick(idx)} key={idx}>
                <QuestionOption
                  displayMode={QuestionOptionType.QUESTION_OPTION_SELECTED}
                  shape1="circle"
                  shape2="circle"
                  shape1Text={no + idx}
                  shape2Text={numberToABC(userAnswers[idx] + 1)}
                  text={options[userAnswers[idx]]}
                  selected
                  onRemoveSelectedOption={(e) => onRemoveAnswer(e, idx)}
                />
              </DragOutMobileContainer>
            );
          }
          return (
            <DragOutMobileContainer key={idx} onClick={() => onItemClick(idx)}>
              <QuestionOption
                displayMode={QuestionOptionType.QUESTION_OPTION_NOT_SELECTED}
                shape1="circle"
                shape2="circle"
                shape1Text={no + idx}
                text="bấm để chọn đáp án"
              />
            </DragOutMobileContainer>
          );
        })}
      </ContentContainer>
      <OptionsListDrawer
        title="Option list"
        selectedOpt={userAnswers}
        numberToString={numberToABC}
        visible={isOpen}
        options={buildOptionsWithKeys(options)}
        onItemClick={answerQuestion}
        onClose={onClose}
        qNumber={no + id}
      />
    </>
  );
});

export const scaleDraggingPoint = (originalImageSize, scaledImageSize, originalPointCoordinate) => {
  const percentWidth = scaledImageSize / originalImageSize;
  return percentWidth * originalPointCoordinate;
};

const buildOptionsWithKeys = (options) => {
  const optionsWithKeys = [];
  let key = 0;
  options.forEach((option) => {
    optionsWithKeys.push({ key, value: option });
    key += 1;
  });
  return optionsWithKeys;
};

const ContentContainer = styled(Box)`
  &.options {
    margin-top: 8px;
  }

  .spot-item {
    padding: 6px 12px;
    &.answered {
      .order {
        background-color: ${colorsV2.blue100};
      }
    }
  }

  &.image-box {
    display: flex;
    justify-content: center;
  }
`;

const ImageWithSpotsContainer = styled.div`
  position: relative;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const DragOutMobileContainer = styled.div`
  width: 100%;
  height: 44px;
  background: ${colorsV2.white100};
  box-sizing: border-box;
  border-radius: 8px;
  align-items: center;
  padding: 9px 12px;

  .delimiter {
    color: ${colorsV2.black100};
    font-weight: 500;
    font-size: 14px;
    margin: 0 8px;
  }

  &.default {
    border: 1px dashed ${colorsV2.gray20};

    .text {
      color: ${colorsV2.gray80};
    }
  }

  &.filled {
    border: 1px solid ${colorsV2.gray20};
  }

  &.correct {
    border: 2px solid ${colorsV2.green40};
  }

  &.no-focus {
    border: 1px solid ${colorsV2.gray20};
  }

  &.in-correct {
    border: 2px solid ${colorsV2.red20};

    .text {
      flex: initial;
    }
  }

  &.missed {
    border: 2px solid ${colorsV2.yellow100};
    .text {
      flex: initial;
    }
  }

  &.empty {
    .answer {
      background: ${colorsV2.gray10};
      border: 1px dashed ${colorsV2.gray60};
    }
  }

  .question-text > span {
    color: ${colorsV2.black100};
  }
`;
